import { useState, useEffect } from 'react';
import { Loader2, ImageOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Auth-aware <img> replacement.
 *
 * A native <img src> tag loads through the browser's image loader, which
 * does NOT go through our patched window.fetch — so it can't attach the
 * `Authorization: Bearer <token>` header that Safari/iPad rely on when ITP
 * drops the session cookie. Result: authenticated image endpoints (e.g. the
 * mechanic-finding photo stream) return 401 and the image never loads.
 *
 * This component fetches the image via fetch() (which the authFetch
 * interceptor decorates with the Bearer token AND sends cookies), turns the
 * response into a blob object-URL, and renders that. Works on every browser
 * regardless of cookie behaviour.
 */
export function AuthImage({ src, alt, className, testId, ...rest }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    setStatus('loading');
    setBlobUrl(null);
    if (!src) {
      setStatus('error');
      return undefined;
    }
    const full = /^https?:\/\//.test(src) ? src : `${BACKEND_URL}${src}`;
    (async () => {
      try {
        const res = await fetch(full, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
        setStatus('ready');
      } catch (_e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [src]);

  if (status === 'loading') {
    return (
      <div className={`flex items-center justify-center bg-slate-100 ${className || ''}`} data-testid={testId ? `${testId}-loading` : undefined}>
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={`flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-400 ${className || ''}`} data-testid={testId ? `${testId}-error` : undefined}>
        <ImageOff className="h-6 w-6" />
        <span className="text-xs">Image unavailable</span>
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} className={className} data-testid={testId} {...rest} />;
}

export default AuthImage;
